import React, { useState } from "react";
import { QueryObserverResult, useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//App
import {
  LookupGroupApiResponse,
  LookupGroupContextData,
  LookupGroupState,
} from "../../../../types/lookupGroup";
import { FormikTypeaheadOption, SortBy } from "../../../../types";
import lookupGroupApi from "../../../../app/model/api/lookupGroupApi";
import LookupGroup from "./LookupGroup";
import {
  selectIsActive,
  selectSearchValue,
  setIsActive,
  setSearchValue,
} from "../../../../store/lookupgroup";
import { SearchValue } from "../../../../store/types";
import {
  useLookupGroupGridQuery,
  useLookupGroupQuery,
  useLookupGroupsData,
} from "./hooks/useLookupGroup";
 
//Common
import LoadingIndicator from "../../../common/VFX-UI/LoadingIndicator/LoadingIndicator";
import { useLookupTypesQuery } from "../LookupType/hooks/useLookupType";

export const LookupGroupContext =
  React.createContext<LookupGroupContextData | null>(null);

const LookupGroupContainer = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<LookupGroupState>({
    lookupTypeId: null,
    lookupGroupId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    currentPage: 1,
    pageSize: 10,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const lookupTypesQuery = useLookupTypesQuery({ isActive: true }, true, true);

  const lookupGroupsGridQuery = useLookupGroupGridQuery(state);

  const lookupGroupQuery = useLookupGroupQuery(state.lookupGroupId);

  const updateLookupGroupId = async (lookupGroupId: string | null) =>
    setState((prevState) => ({
      ...prevState,
      lookupGroupId: lookupGroupId,
    }));

  const updateLookupTypeId = async (lookupTypeId: string | null) =>
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      lookupTypeId: lookupTypeId,
    }));

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) =>
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupGroupApi.delete(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupGroupApi.activate(value);
    },
  });

  const lookupGroups = useLookupGroupsData(lookupGroupsGridQuery);

  const contextValue: LookupGroupContextData = {
    data: lookupGroupQuery?.data?.result,
    id: state.lookupGroupId,
    lookupTypes: lookupTypesQuery?.data as FormikTypeaheadOption[],
    lookupTypeId: state.lookupTypeId,
    activeToggle: state.activeToggle,
    searchValue: state.searchPayload ?? undefined,
    paging: {
      currentPage: state.currentPage,
      totalSize: lookupGroups?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    setCurrentPageSize: updateCurrentPageSize,
    setId: updateLookupGroupId,
    setLookupTypeId: updateLookupTypeId,
    setActiveToggleValue: updateActiveToggle,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setSortBy: updateSortBy,
    refetchGrid: () =>
      lookupGroupsGridQuery.refetch() as Promise<
        QueryObserverResult<LookupGroupApiResponse>
      >,
  };

  if (lookupTypesQuery?.isLoading || lookupGroupsGridQuery?.isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <LookupGroupContext.Provider value={contextValue}>
      <LookupGroup
        data={lookupGroups?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </LookupGroupContext.Provider>
  );
};

export default LookupGroupContainer;
